// Composant Radar Chart Risques (HALTES) avec Recharts
// Echelle normalisee 0-8 pour equilibrer visuellement les axes
// Axes N/A (max=0) filtres automatiquement — le radar s'adapte

import {
  RadarChart as RechartsRadar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Progress } from '@/components/ui/progress'

// Echelle fixe du radar Risques : 0 a 8
const RADAR_MAX_SCALE = 8

export interface ScoresAxes {
  H: number
  A: number
  LT: number
  E: number
  S: number
}

export interface MaxParAxe {
  H: number
  A: number
  LT: number
  E: number
  S: number
}

export interface RadarChartProps {
  scores: ScoresAxes
  maxScores?: MaxParAxe
  showLabels?: boolean
  height?: number
}

const AXES_CONFIG = [
  { key: 'H', label: 'Hygiène' },
  { key: 'A', label: 'Accès' },
  { key: 'LT', label: 'Livraisons\net Tâches' },
  { key: 'E', label: 'Énergies' },
  { key: 'S', label: 'Secours' },
]

// Fallback barres horizontales si < 3 axes evaluables
function FallbackBars({ data }: { data: Array<{ subject: string; rawScore: number; max: number }> }) {
  return (
    <div className="space-y-3 py-4">
      {data.map((item) => {
        const percent = item.max > 0 ? (item.rawScore / item.max) * 100 : 0
        return (
          <div key={item.subject}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-foreground">{item.subject.replace('\n', ' ')}</span>
              <span className="text-xs text-muted-foreground">{item.rawScore}/{item.max}</span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>
        )
      })}
    </div>
  )
}

export default function RadarChart({
  scores,
  maxScores,
  showLabels = true,
  height = 300,
}: RadarChartProps) {
  // Filtrer les axes dont max > 0 (evaluables uniquement)
  const activeAxes = AXES_CONFIG.filter((axe) => {
    const max = maxScores?.[axe.key as keyof MaxParAxe] || 0
    return max > 0
  })

  // Construire les donnees uniquement pour les axes actifs
  const data = activeAxes.map((axe) => {
    const rawScore = scores[axe.key as keyof ScoresAxes] || 0
    const max = maxScores?.[axe.key as keyof MaxParAxe] || 1
    const normalizedScore = (rawScore / max) * RADAR_MAX_SCALE
    return {
      subject: axe.label,
      score: Math.round(normalizedScore * 10) / 10,
      rawScore,
      max,
      fullMark: RADAR_MAX_SCALE,
    }
  })

  // Pas assez d'axes pour un radar (< 3) → fallback en barres
  if (data.length < 3) {
    return <FallbackBars data={data} />
  }

  const radarColor = 'var(--primary)'

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <RechartsRadar
          data={data}
          margin={{ top: 20, right: 40, bottom: 20, left: 40 }}
        >
          <PolarGrid
            stroke="var(--border)"
            strokeWidth={1}
            gridType="polygon"
          />
          <PolarAngleAxis
            dataKey="subject"
            tick={({ x, y, payload }: any) => (
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="var(--muted-foreground)"
                style={{ fontSize: '11px', fontWeight: 500 }}
              >
                {payload.value.includes('\n') ? (
                  payload.value.split('\n').map((line: string, i: number) => (
                    <tspan key={i} x={x} dy={i === 0 ? 0 : 14}>
                      {line}
                    </tspan>
                  ))
                ) : (
                  payload.value
                )}
              </text>
            )}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, RADAR_MAX_SCALE]}
            tick={false}
            axisLine={false}
          />
          <Radar
            name="Score"
            dataKey="score"
            stroke={radarColor}
            fill={radarColor}
            fillOpacity={0.35}
            strokeWidth={3.5}
            dot={{
              r: 4,
              fill: radarColor,
              stroke: 'var(--card)',
              strokeWidth: 2,
            }}
          />
          {showLabels && (
            <Tooltip
              formatter={(_value: any, _name: any, entry: any) => {
                const item = entry.payload
                return [
                  `${item.score} / ${item.fullMark}`,
                  item.subject.replace('\n', ' '),
                ]
              }}
              contentStyle={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '13px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
            />
          )}
        </RechartsRadar>
      </ResponsiveContainer>
    </div>
  )
}

// Version simplifiee pour mobile
export function RadarChartMini({ scores, maxScores }: { scores: ScoresAxes; maxScores?: MaxParAxe }) {
  return <RadarChart scores={scores} maxScores={maxScores} height={250} showLabels={false} />
}

// Composant legende personnalisee avec points
export function RadarLegend({ scores, maxScores, compact = false }: { scores: ScoresAxes; maxScores?: MaxParAxe; compact?: boolean }) {
  const getScoreColor = (score: number, max: number) => {
    if (max === 0) return 'text-muted-foreground bg-muted'
    const ratio = score / max
    if (ratio >= 0.7) return 'text-primary bg-primary/20'
    if (ratio >= 0.4) return 'text-primary bg-primary/10'
    return 'text-primary bg-primary/5'
  }

  if (compact) {
    return (
      <div className="grid grid-cols-5 gap-1 mt-2">
        {AXES_CONFIG.map((axe) => {
          const score = scores[axe.key as keyof ScoresAxes] || 0
          const max = maxScores?.[axe.key as keyof MaxParAxe] || 0
          const isNA = max === 0
          return (
            <div
              key={axe.key}
              className={`py-1 px-1.5 rounded text-center ${getScoreColor(score, max)} ${isNA ? 'opacity-60' : ''}`}
            >
              <div className={`text-xs font-medium ${isNA ? '' : 'opacity-75'}`}>
                {axe.label.replace('\n', ' ')}
                {isNA && <span className="block text-xs">(N/A)</span>}
              </div>
              <div className="text-xs font-bold">{isNA ? '—' : score}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {AXES_CONFIG.map((axe) => {
        const score = scores[axe.key as keyof ScoresAxes] || 0
        const max = maxScores?.[axe.key as keyof MaxParAxe] || 0
        const isNA = max === 0
        return (
          <div
            key={axe.key}
            className={`p-3 rounded-lg ${getScoreColor(score, max)} ${isNA ? 'opacity-60' : ''}`}
          >
            <div className={`text-xs font-medium ${isNA ? '' : 'opacity-75'}`}>
              {axe.label.replace('\n', ' ')}
              {isNA && <span className="ml-1 text-xs">(N/A)</span>}
            </div>
            <div className="text-lg font-bold">
              {isNA ? 'N/A' : `${score}/${max} pts`}
            </div>
          </div>
        )
      })}
    </div>
  )
}
